// src/components/operationsAssociate/CreateBOQ.jsx
import React, { useState, useEffect } from "react";
import { createBOQ } from "../../api/OAprocurement-api";
import { getVendors } from "../../api/vendorApi"; // Import getVendors

const BOQForm = ({ onClose, onSuccess }) => {
  const [issueDate, setIssueDate] = useState("");
  const [preparedBy, setPreparedBy] = useState("");
  const [items, setItems] = useState([
    { itemNo: 1, description: "", unit: "", quantity: 0, unitRate: 0 },
  ]);
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);

  // NEW: Vendor selection states
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [vendorError, setVendorError] = useState(null);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // NEW: Fetch vendors on component mount
  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setVendorError("Authentication token not found. Please log in.");
          return;
        }

        console.log("Fetching vendors for BOQ...");
        const response = await getVendors();
        console.log("Vendors response:", response);
        
        let vendorData = [];
        if (Array.isArray(response)) {
          vendorData = response;
        } else if (response?.data) {
          vendorData = Array.isArray(response.data) ? response.data : response.data.data || [];
        }
        
        console.log("Processed vendor data:", vendorData);
        setVendors(Array.isArray(vendorData) ? vendorData : []);
      } catch (error) {
        console.error("Failed to load vendors:", error);
        setVendorError(`Failed to load vendors: ${error.message}`);
        setVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendors();
  }, []);

  // NEW: Handle vendor selection
  const handleVendorToggle = (vendor) => {
    const isSelected = selectedVendors.some(v => v._id === vendor._id);
    
    if (isSelected) {
      setSelectedVendors(selectedVendors.filter(v => v._id !== vendor._id));
    } else {
      setSelectedVendors([...selectedVendors, vendor]);
    }
  };

  // NEW: Select all vendors
  const handleSelectAllVendors = () => {
    if (selectedVendors.length === vendors.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors([...vendors]);
    }
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      { itemNo: items.length + 1, description: "", unit: "", quantity: 0, unitRate: 0 },
    ]);
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  const subtotal = items.reduce(
    (acc, item) => acc + (Number(item.quantity) * Number(item.unitRate) || 0),
    0
  );
  const taxRate = 0.16;
  const taxes = subtotal * taxRate;
  const grandTotal = subtotal + taxes;

  // NEW: Validation
  const validateForm = () => {
    const errors = {};
    if (!issueDate) errors.issueDate = "Issue date is required.";
    if (!preparedBy.trim()) errors.preparedBy = "Prepared by field is required.";
    if (selectedVendors.length === 0) {
      errors.vendors = "Please select at least one vendor to notify.";
    }
    
    // Validate items
    items.forEach((item, idx) => {
      if (!item.description.trim()) {
        errors[`description-${idx}`] = "Description is required.";
      }
      if (!item.unit.trim()) {
        errors[`unit-${idx}`] = "Unit is required.";
      }
      if (!item.quantity || Number(item.quantity) <= 0) {
        errors[`quantity-${idx}`] = "Quantity must be greater than zero.";
      }
      if (!item.unitRate || Number(item.unitRate) <= 0) {
        errors[`unitRate-${idx}`] = "Unit rate must be greater than zero.";
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // UPDATED: Include selected vendors in BOQ data
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const formData = new FormData();
      formData.append("issueDate", issueDate);
      formData.append("preparedBy", preparedBy);
      formData.append("notes", notes);

      items.forEach((item, index) => {
        formData.append(`items[${index}][itemNo]`, Number(item.itemNo));
        formData.append(`items[${index}][description]`, item.description);
        formData.append(`items[${index}][unit]`, item.unit);
        formData.append(`items[${index}][quantity]`, Number(item.quantity));
        formData.append(`items[${index}][unitRate]`, Number(item.unitRate));
      });

      files.forEach((file) => formData.append("files", file));

      // NEW: Include selected vendors
      formData.append("notifiedVendors", JSON.stringify(selectedVendors.map(vendor => ({
        vendorId: vendor._id,
        vendorName: vendor.name,
        vendorCode: vendor.vendorCode || vendor.code || '',
        email: vendor.email || vendor.contactEmail,
        contactPerson: vendor.contactPerson || vendor.contactName
      }))));

      formData.append("vendorEmails", JSON.stringify(
        selectedVendors.map(vendor => vendor.email || vendor.contactEmail).filter(Boolean)
      ));

      await createBOQ(formData);

      console.log("Purchase Order created successfully!");
      alert(`Purchase Order created successfully and sent to ${selectedVendors.length} vendor(s)!`);
      
      if (onSuccess) {
        await onSuccess();
      }
    } catch (err) {
      console.error("Error creating Purchase Order:", err.response?.data || err.message);
      alert("Error creating Purchase Order");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 bg-white rounded-xl shadow-lg max-w-6xl mx-auto max-h-[90vh] overflow-y-auto">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Purchase Order</h2>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block font-medium mb-1">Issue Date *</label>
          <input
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            className={`border p-3 w-full rounded-lg ${formErrors.issueDate ? "border-red-500" : "border-gray-300"}`}
            required
          />
          {formErrors.issueDate && <p className="text-red-500 text-sm mt-1">{formErrors.issueDate}</p>}
        </div>
        <div>
          <label className="block font-medium mb-1">Prepared By *</label>
          <input
            type="text"
            value={preparedBy}
            onChange={(e) => setPreparedBy(e.target.value)}
            className={`border p-3 w-full rounded-lg ${formErrors.preparedBy ? "border-red-500" : "border-gray-300"}`}
            placeholder="Enter preparer name"
            required
          />
          {formErrors.preparedBy && <p className="text-red-500 text-sm mt-1">{formErrors.preparedBy}</p>}
        </div>
      </div>

      {/* NEW: Vendor Selection Section */}
      <div className="mb-6">
        <label className="block font-medium mb-2">Select Vendors to Notify</label>
        
        {vendorError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3">
            {vendorError}
          </div>
        )}

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowVendorDropdown(!showVendorDropdown)}
            className={`w-full border rounded-lg px-3 py-2 text-left bg-white flex justify-between items-center ${formErrors.vendors ? "border-red-500" : "border-gray-300"} ${loadingVendors ? 'cursor-wait' : 'cursor-pointer'}`}
            disabled={loadingVendors}
          >
            <span className="text-gray-700">
              {loadingVendors ? "Loading vendors..." : 
               selectedVendors.length === 0 ? "Select vendors to notify..." :
               `${selectedVendors.length} vendor(s) selected`}
            </span>
            <svg className={`w-4 h-4 transition-transform ${showVendorDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showVendorDropdown && !loadingVendors && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              <div className="p-2 border-b border-gray-200">
                <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                  <input
                    type="checkbox"
                    checked={selectedVendors.length === vendors.length && vendors.length > 0}
                    onChange={handleSelectAllVendors}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="font-medium text-gray-700">
                    Select All ({vendors.length} vendors)
                  </span>
                </label>
              </div>

              {vendors.length === 0 ? (
                <div className="p-4 text-gray-500 text-center">No vendors available</div>
              ) : (
                vendors.map((vendor) => (
                  <label key={vendor._id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedVendors.some(v => v._id === vendor._id)}
                      onChange={() => handleVendorToggle(vendor)}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{vendor.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {vendor.vendorCode || vendor.code ? `Code: ${vendor.vendorCode || vendor.code} | ` : ''}
                        {vendor.email || vendor.contactEmail || 'No email'}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        {formErrors.vendors && <p className="text-red-500 text-sm mt-1">{formErrors.vendors}</p>}

        {selectedVendors.length > 0 && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-sm font-medium text-green-800 mb-2">
              Selected Vendors ({selectedVendors.length}):
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedVendors.map((vendor) => (
                <span key={vendor._id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {vendor.name}
                  <button
                    type="button"
                    onClick={() => handleVendorToggle(vendor)}
                    className="ml-1 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <h3 className="text-lg font-semibold mt-6 mb-4">Items Table</h3>
      <div className="overflow-x-auto">
        <table className="w-full border mb-4">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2">Item No</th>
              <th className="border p-2">Description *</th>
              <th className="border p-2">Unit *</th>
              <th className="border p-2">Quantity *</th>
              <th className="border p-2">Unit Rate *</th>
              <th className="border p-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                <td className="border p-2 text-center">{item.itemNo}</td>
                <td className="border p-2">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleItemChange(i, "description", e.target.value)}
                    className={`w-full p-1 border rounded ${formErrors[`description-${i}`] ? "border-red-500" : "border-gray-300"}`}
                    placeholder="Item description"
                    required
                  />
                  {formErrors[`description-${i}`] && <p className="text-red-500 text-xs mt-1">{formErrors[`description-${i}`]}</p>}
                </td>
                <td className="border p-2">
                  <input
                    type="text"
                    value={item.unit}
                    onChange={(e) => handleItemChange(i, "unit", e.target.value)}
                    className={`w-full p-1 border rounded ${formErrors[`unit-${i}`] ? "border-red-500" : "border-gray-300"}`}
                    placeholder="e.g., pcs, kg"
                    required
                  />
                  {formErrors[`unit-${i}`] && <p className="text-red-500 text-xs mt-1">{formErrors[`unit-${i}`]}</p>}
                </td>
                <td className="border p-2">
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(i, "quantity", e.target.value)}
                    className={`w-full p-1 border rounded ${formErrors[`quantity-${i}`] ? "border-red-500" : "border-gray-300"}`}
                    required
                  />
                  {formErrors[`quantity-${i}`] && <p className="text-red-500 text-xs mt-1">{formErrors[`quantity-${i}`]}</p>}
                </td>
                <td className="border p-2">
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={item.unitRate}
                    onChange={(e) => handleItemChange(i, "unitRate", e.target.value)}
                    className={`w-full p-1 border rounded ${formErrors[`unitRate-${i}`] ? "border-red-500" : "border-gray-300"}`}
                    required
                  />
                  {formErrors[`unitRate-${i}`] && <p className="text-red-500 text-xs mt-1">{formErrors[`unitRate-${i}`]}</p>}
                </td>
                <td className="border p-2 text-center font-medium">
                  ${(item.quantity * item.unitRate || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={handleAddItem}
        className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg shadow transform hover:scale-105 transition-all duration-200 mb-6"
      >
        + Add Item
      </button>

      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="text-right space-y-2">
          <p className="text-lg">Subtotal: <strong>${subtotal.toFixed(2)}</strong></p>
          <p className="text-lg">Taxes (16%): <strong>${taxes.toFixed(2)}</strong></p>
          <p className="text-xl font-bold text-orange-600 border-t pt-2">Grand Total: <strong>${grandTotal.toFixed(2)}</strong></p>
        </div>
      </div>

      <div className="mb-6">
        <label className="block font-medium mb-1">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg"
          rows={3}
          placeholder="Additional notes or comments..."
        />
      </div>

      <div className="mb-6">
        <label className="block font-medium mb-1">Attach Supporting Documents</label>
        <input 
          type="file" 
          multiple 
          onChange={handleFileChange} 
          className="w-full p-2 border border-gray-300 rounded-lg"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
        />
      </div>

      <div className="flex gap-4 justify-end">
        <button 
          type="button" 
          onClick={onClose}
          className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg shadow transform hover:scale-105 transition-all duration-200"
        >
          Submit PO
        </button>
      </div>
    </form>
  );
};

export default BOQForm;
